import type { QueryClient } from '@tanstack/react-query';
import type { ConversationDetail, MessageFlag } from '@/api';
import { applyLabelKeywordEdit, type LabelKeywordEdit } from '@/cache/messagePatches';
import { conversationKeys } from './queryKeys';

/** A flag change, as the mail view's `setFlags` mutation describes one. */
export interface ConversationFlagUpdate {
  add?: readonly MessageFlag[];
  remove?: readonly MessageFlag[];
}

function applyFlags(flags: readonly MessageFlag[], update: ConversationFlagUpdate): MessageFlag[] {
  const next = flags.filter((flag) => !(update.remove ?? []).includes(flag));
  for (const flag of update.add ?? []) if (!next.includes(flag)) next.push(flag);
  return next;
}

/**
 * Patch a flag change into every cached conversation of the account that
 * holds one of `messageIds` — the reading pane's stack and its toolbar star
 * move with the mutation instead of waiting for a refetch.
 *
 * For the mail view's optimistic flag mutation (`onMutate`), beside the list
 * and detail patches it already makes. Returns a function that restores what
 * was there, for `onError`. The conversation's own summary is recomputed the
 * way the server computes it (plan/16 §2): `seen` iff every in-folder member
 * is seen, `flagged` iff any is, and `unreadCount` over in-folder members.
 */
export function patchConversationFlags(
  queryClient: QueryClient,
  accountId: string,
  messageIds: readonly string[],
  update: ConversationFlagUpdate,
): () => void {
  const ids = new Set(messageIds);
  const previous = queryClient.getQueriesData<ConversationDetail>({
    queryKey: conversationKeys.scope(accountId),
  });
  for (const [key, detail] of previous) {
    if (detail === undefined || !detail.messages.some((message) => ids.has(message.id))) continue;
    const messages = detail.messages.map((message) =>
      ids.has(message.id) ? { ...message, flags: applyFlags(message.flags, update) } : message,
    );
    const members = messages.filter((message) => message.inFolder);
    const flags: MessageFlag[] = detail.conversation.flags.filter(
      (flag) => flag !== 'seen' && flag !== 'flagged',
    );
    if (members.length > 0 && members.every((message) => message.flags.includes('seen'))) flags.push('seen');
    if (members.some((message) => message.flags.includes('flagged'))) flags.push('flagged');
    queryClient.setQueryData<ConversationDetail>(key, {
      ...detail,
      messages,
      conversation: {
        ...detail.conversation,
        flags,
        unreadCount: members.filter((message) => !message.flags.includes('seen')).length,
      },
    });
  }
  return () => {
    for (const [key, detail] of previous) queryClient.setQueryData(key, detail);
  };
}

/**
 * Patch a label change into every cached conversation of the account that
 * holds one of `messageIds` (`undefined`: every one) — the pane's chips move
 * with the picker and with a live frame, and roll back with the list. The
 * summary's union is recomputed from the messages, as the server builds it.
 */
export function patchConversationLabels(
  queryClient: QueryClient,
  accountId: string,
  messageIds: readonly string[] | undefined,
  edit: LabelKeywordEdit,
): void {
  const ids = messageIds ? new Set(messageIds) : undefined;
  for (const [key, detail] of queryClient.getQueriesData<ConversationDetail>({
    queryKey: conversationKeys.scope(accountId),
  })) {
    if (detail === undefined || !Array.isArray(detail.messages)) continue;
    let changed = false;
    const messages = detail.messages.map((message) => {
      if (ids !== undefined && !ids.has(message.id)) return message;
      const labelKeywords = applyLabelKeywordEdit(message.labelKeywords, edit);
      if (labelKeywords === message.labelKeywords) return message;
      changed = true;
      return { ...message, labelKeywords };
    });
    if (!changed) continue;
    const union: string[] = [];
    for (const message of messages) {
      for (const keyword of message.labelKeywords ?? []) {
        if (!union.some((held) => held.toLowerCase() === keyword.toLowerCase())) union.push(keyword);
      }
    }
    // Messages the detail does not hold (older than the newest 100) keep
    // whatever the union already said about them.
    const kept =
      detail.omittedCount > 0 && edit.set === undefined
        ? applyLabelKeywordEdit(detail.conversation.labelKeywords, {
            ...(edit.remove ? { remove: edit.remove } : {}),
            ...(edit.rename ? { rename: edit.rename } : {}),
          })
        : [];
    for (const keyword of kept) {
      if (!union.some((held) => held.toLowerCase() === keyword.toLowerCase())) union.push(keyword);
    }
    queryClient.setQueryData<ConversationDetail>(key, {
      ...detail,
      messages,
      conversation: { ...detail.conversation, labelKeywords: union },
    });
  }
}

/**
 * Mark every cached conversation of the account stale, and refetch the ones
 * on screen IN PLACE: after new mail, an expunge or a stream gap, when a
 * patch cannot say what changed. The open stack keeps showing what it has
 * until the answer lands (a refetch keeps a query's data), and a refetch
 * already out is joined rather than aborted — an aborted request costs the
 * backend its IMAP connection. `''` means every account.
 */
export function invalidateConversations(queryClient: QueryClient, accountId: string): Promise<void> {
  return queryClient.invalidateQueries(
    accountId === ''
      ? { queryKey: ['account'], predicate: (query) => query.queryKey[2] === 'conversation' }
      : { queryKey: conversationKeys.scope(accountId) },
    { cancelRefetch: false },
  );
}
